import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface GlobalEContextType {
  isScriptLoaded: boolean;
  loadScript: boolean;
  notifyScriptError: () => void;
  notifyScriptLoaded: () => void;
  scriptLoadError: boolean;
  setLoadScript: (value: boolean) => void;
}

export const GlobalEContext = createContext<GlobalEContextType | undefined>(
  undefined,
);

export const useGlobalEContext = () => {
  const context = useContext(GlobalEContext);
  if (!context) {
    throw new Error('useGlobalEContext must be used within a GlobalEProvider');
  }
  return context;
};

export const GlobalEProvider = ({ children }: { children: ReactNode }) => {
  const [loadScript, setLoadScriptState] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [scriptLoadError, setScriptLoadError] = useState(false);

  const setLoadScript = useCallback((value: boolean) => {
    setLoadScriptState(value);
    setScriptLoadError(false);
    if (!value) {
      setIsScriptLoaded(false);
    }
  }, []);

  const notifyScriptLoaded = useCallback(() => {
    setScriptLoadError(false);
    setIsScriptLoaded(true);
  }, []);

  const notifyScriptError = useCallback(() => {
    setIsScriptLoaded(false);
    setScriptLoadError(true);
  }, []);

  const value = useMemo(
    () => ({
      isScriptLoaded,
      loadScript,
      notifyScriptError,
      notifyScriptLoaded,
      scriptLoadError,
      setLoadScript,
    }),
    [
      isScriptLoaded,
      loadScript,
      notifyScriptError,
      notifyScriptLoaded,
      scriptLoadError,
      setLoadScript,
    ],
  );

  return (
    <GlobalEContext.Provider value={value}>{children}</GlobalEContext.Provider>
  );
};
